import pick from 'lodash/pick';
import moment from 'moment-timezone';
import queryString from 'query-string';

import {getTimezoneToday, getUtcOffset} from 'common/util/date';

import {getIsWorkspaceGlobal, getProjectId, getWorkspaceId} from './report/util';
import {isStandalone, isTestEnv} from './report/util/env';
import {
  DownloadMode,
  FeatureGate,
  FeatureGatesValue,
  ProjectFeatureFlag,
  RestrictedReport,
  RestrictEntitySaving,
  UserFeatureFlag,
} from './report/constants';
import {installDevFetchCache} from './report/queries/utils/fetch-cache';
import {currentProjectStore, ProjectDetails} from './report/stores/project-details';
import {featuresStore} from './report/stores/features';
import {getUserIdentityFromCookie} from './report/util/user-identity-from-cookie';
import {reporter} from './report/error-reporting';
import {getMpReportGlobals, MpReportGlobals} from './report/util/mp-report-globals';

export class MPContext {
  private projectAsOfDate: string;

  _projectId: number;
  isStandalone: boolean;
  urlOptions: {[key: string]: string};
  blocking: MpReportGlobals['blocking'];
  currentProject: ProjectDetails;
  downloadViewMode: DownloadMode;
  downloadTitle?: string;
  downloadDisplayPreferences?: MpReportGlobals['download_display_preferences'];
  projectTimezone: string;
  projectToken: string;
  userEmail: string;
  userId: number;
  userName: string;
  userPermissions: string[];
  utcOffset: number;
  organizationId: number;
  workspaceId: number;
  isWorkspaceGlobal: boolean;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  constructor({projectId = null} = {}) {
    this.projectId = parseInt(projectId || getProjectId(), 10);
    this.isStandalone = isStandalone();
    this.workspaceId = parseInt(getWorkspaceId(), 10);
    this.isWorkspaceGlobal = getIsWorkspaceGlobal();

    if (this.isStandalone) {
      this.urlOptions = queryString.parse(window.location.search);

      this.blocking = {
        is_blocked_events: false,
        is_blocked_people: false,
        label: ``,
      };
      this.downloadViewMode = Number(this.urlOptions.download) || DownloadMode.None;
      this.projectTimezone = moment.tz.guess();

      const user = getUserIdentityFromCookie() || {id: 1, email: `dev.test@mixpanel.com`, name: `Unknown`};
      this.userEmail = user.email;
      this.userName = user.name;
      this.userId = user.id;
      this.userPermissions = [];
      this.utcOffset = -new Date().getTimezoneOffset();

      if (`fetch_cache` in this.urlOptions) {
        // hydrate cache (default) - respond from cache if match, fetch in background and update cache for next time
        // simple cache - respond from cache if match, otherwise fetch from network and respond
        const hydrate = this.urlOptions.fetch_cache !== `simple`;
        installDevFetchCache(hydrate);
      }
    } else {
      const globals = getMpReportGlobals();
      // Globals
      this.blocking = globals.blocking;
      this.downloadViewMode = globals.download_view_mode || DownloadMode.None;
      if (this.downloadViewMode !== DownloadMode.None) {
        this.downloadTitle = globals.download_title;
        this.downloadDisplayPreferences = globals.download_display_preferences;
      }

      this.projectAsOfDate = globals.project_as_of_date; // used in this.getProjectToday()

      this.organizationId = Number(globals.organization_id);
      const projectTimezone = globals.timezones.find((tz) => tz[0] === globals.project_timezone);
      this.projectTimezone = (projectTimezone && projectTimezone[1]) || moment.tz.guess(); // fallback to browser timezone
      this.projectToken = globals.project_token;
      this.userEmail = globals.user_email;
      this.userId = Number(globals.user_id);
      this.userName = globals.user_name;
      this.userPermissions = globals.permissions;

      // GOOT: webapp server is providing incorrect/oudated UTC offset values, so as a quick fix we use an up-to-date moment-timezone to determine UTC offset
      this.utcOffset = isTestEnv() ? globals.utc_offset : getUtcOffset(this.projectTimezone);
      if (globals.utc_offset !== this.utcOffset) {
        console.warn(
          `Discrepancy between server-provided UTC offset (${globals.utc_offset}) and frontend (${this.utcOffset})!`,
        );
      }
    }
  }

  get projectId() {
    return this._projectId;
  }

  set projectId(projectId) {
    if (projectId !== this._projectId) {
      this._projectId = projectId;
      if (this.projectId) {
        currentProjectStore.subscribe(() => {
          this.currentProject = currentProjectStore.value;
          if (this.currentProject) {
            this.organizationId = this.currentProject.organizationId;
            this.projectTimezone = this.currentProject.timezone;
            this.utcOffset = this.currentProject.utcOffset;
          }
        });
        currentProjectStore.fetch({projectId: this.projectId});
      }
    }
  }

  private _warnFeaturesStoreNotReady(accessFnMsg: string) {
    reporter.warn(`featuresStore hasn't loaded, ${accessFnMsg} may return incorrect value`);
  }

  hasProjectFeatureFlag(name: ProjectFeatureFlag): boolean {
    if (featuresStore.value) {
      return featuresStore.value.hasProjectFeatureFlag(name);
    } else {
      this._warnFeaturesStoreNotReady(`hasProjectFeatureFlag('${name}')`);
      return false;
    }
  }

  hasUserFeatureFlag(name: UserFeatureFlag): boolean {
    if (featuresStore.value) {
      return featuresStore.value.hasUserFeatureFlag(name);
    } else {
      this._warnFeaturesStoreNotReady(`hasUserFeatureFlag('${name}')`);
      return false;
    }
  }

  hasFeatureGate(name: FeatureGate): boolean {
    if (featuresStore.value) {
      return !!featuresStore.value.getFeatureGate(name);
    } else {
      this._warnFeaturesStoreNotReady(`hasFeatureGate('${name}')`);
      return false;
    }
  }

  getFeatureGateValue<K extends keyof FeatureGatesValue>(name: K): FeatureGatesValue[K] | null {
    if (featuresStore.value) {
      return featuresStore.value.getFeatureGate(name);
    } else {
      this._warnFeaturesStoreNotReady(`getFeatureGateValue('${name}')`);
      return null;
    }
  }

  getFeatureGateName(): string {
    if (featuresStore.value) {
      return featuresStore.value.getFeatureGate(name);
    } else {
      this._warnFeaturesStoreNotReady(`getFeatureGateName()`);
      return ``;
    }
  }

  hasPermission(permission: string): boolean {
    if (this.currentProject) {
      return this.currentProject.hasPermission(permission);
    } else {
      return this.userPermissions.includes(permission);
    }
  }

  hasAccessToReport(name: RestrictedReport): boolean {
    if (featuresStore.value) {
      return featuresStore.value.hasAccessToReport(name);
    } else {
      this._warnFeaturesStoreNotReady(`hasAccessToReport('${name}')`);
      return false;
    }
  }

  canSaveEntity(name: RestrictEntitySaving): boolean {
    if (featuresStore.value) {
      return featuresStore.value.canSaveEntity(name);
    } else {
      this._warnFeaturesStoreNotReady(`canSaveEntity('${name}')`);
      return false;
    }
  }

  canUseCohorts(): boolean {
    return this.hasAccessToReport(RestrictedReport.Cohorts) || this.hasProjectFeatureFlag(ProjectFeatureFlag.Cohorts);
  }

  canUseDataGroups(): boolean {
    return this.hasProjectFeatureFlag(ProjectFeatureFlag.B2BAnalytics) || this.hasFeatureGate(FeatureGate.B2BAnalytics);
  }

  /** Return today in project time, optionally aligned to start or end of day. */
  getProjectToday(align: `start` | `end` = null): Date {
    return getTimezoneToday(this.projectTimezone, this.projectAsOfDate, align);
  }

  public getUserTrackingData() {
    return pick(this, [`projectId`, `userEmail`, `userId`, `userName`]);
  }
}

// singleton mpContext so we don't have to $child thread mpContext through the component chain
// mpContext as a pattern will be cleaned up later but this is the lesser evil
let mpContext: MPContext = null;

export function getMpContext(): MPContext {
  if (!mpContext) {
    mpContext = new MPContext();
  }
  return mpContext;
}
